// Head tags and FAQ for the "Is {service} down?" pages at /tools/is-website-down/{domain}.
// The service list is NOT imported here (only its type): the route's loader imports it
// lazily, so the 300-service list stays out of the main bundle.
// Curated services (DOWN_SITES) get an indexable page with their own description and a
// FAQPage JSON-LD. Any other domain still works, but with a generic description and noindex.
import { EDGE_LOCATION_COUNT } from '@/lib/check-locations'
import type { DownSite } from '@/lib/down-sites'
import { clampDescription, DESCRIPTION_MAX, firstThatFits } from '@/lib/meta'
import { faqPage, jsonLdScript, webPage, type FaqEntry } from '@/lib/schema'

const BASE_URL = 'https://uptrack.app'

/** The two questions every page answers, visibly and in the FAQPage JSON-LD. */
export function downPageFaq(name: string, domain: string, statusPage?: string): FaqEntry[] {
  return [
    {
      question: `Is ${name} down right now?`,
      answer: `This page requests ${domain} from ${EDGE_LOCATION_COUNT} locations on six continents at the same time. Each location reports the HTTP status code and the response time. When most locations fail, ${name} is down for everyone. When only a few fail, the problem is regional. When every location gets an answer, ${name} is up and the problem is probably between you and the site.`,
    },
    {
      question: `What should I do if ${name} is down for me only?`,
      answer: `Reload without the browser cache (Ctrl+Shift+R, or Cmd+Shift+R on a Mac) or open ${domain} in a private window. Flush your DNS cache, or switch to a public DNS resolver such as 1.1.1.1 or 8.8.8.8. Turn your VPN or proxy off, or on, to take a different route, and try mobile data instead of Wi-Fi to rule out your ISP. If other sites fail too, contact your ISP.`,
    },
    ...(statusPage
      ? [
          {
            question: `Where is the official ${name} status page?`,
            answer: `The official ${name} status page is ${statusPageHost(statusPage)}. It shows what the company reports and is the source of truth for incidents it confirms. This page is independent: Uptrack is not affiliated with ${name}. It requests the public website ${domain} itself, so it can show a problem before the status page changes. It does not test sign-in or API calls, so use the official page for those.`,
          },
        ]
      : []),
    {
      question: `How do I get an alert when ${name} is down?`,
      answer: `Many vendors let you subscribe on their official status page, and that tells you what the vendor confirms. To learn about a failure on your side, add an HTTP monitor in Uptrack for the address your app depends on. It checks every 30 seconds and alerts you by email or chat when a check fails. Paid plans check from several locations and alert only when several of them confirm the failure. See the pricing page for plans.`,
    },
  ]
}

/**
 * The short answer shown directly under the H1 of every "Is {name} down?" page, in the HTML a crawler reads
 * (the live verdict only appears for a real visitor after the check runs). It says what the page does and how
 * to read the result, the same logic as the first FAQ answer, in two sentences.
 */
export function downPageAnswer(name: string, domain: string): string {
  return `Short answer: this page checks ${domain} from ${EDGE_LOCATION_COUNT} locations on six continents right now. If most locations fail, ${name} is down for everyone; if only a few fail, the problem is regional; if every location answers, ${name} is up and the problem is probably between you and the site.`
}

/** `status.claude.com` for `https://status.claude.com`: the text a reader sees. */
export function statusPageHost(statusPage: string): string {
  return statusPage.replace(/^https:\/\//, '')
}

/** The short paragraph under "<name> status page": the official page is the source of truth, this page is independent. */
export function downPageStatusNote(name: string, domain: string, statusPage: string): string {
  return `The official ${name} status page is ${statusPageHost(statusPage)}. It shows what ${name} reports, including incidents and the state of each component, and it is the source of truth. This page is independent: Uptrack is not affiliated with ${name}. It requests ${domain} from ${EDGE_LOCATION_COUNT} locations itself, so it shows what we see from outside. It does not test sign-in or API calls.`
}

/** A service with an official status page names it, because people search for "<name> status page". */
export function downPageTitle(name: string, hasStatusPage = false): string {
  return firstThatFits([
    ...(hasStatusPage ? [`Is ${name} Down? Live Check and Status Page — Uptrack`] : []),
    `Is ${name} Down Right Now? Check from ${EDGE_LOCATION_COUNT} Locations — Uptrack`,
    `Is ${name} Down? Check from ${EDGE_LOCATION_COUNT} Locations — Uptrack`,
    `Is ${name} Down Right Now? — Uptrack`,
    `Is ${name} Down? — Uptrack`,
  ])
}

/** The page description when it fits; else its first sentence plus what the page does. */
export function downPageMetaDescription(name: string, description: string): string {
  if (description.length <= DESCRIPTION_MAX) return description
  // A brand or abbreviation can hold the mark (Kahoot!, Warner Bros.), so a sentence needs some length.
  const first = /^.{40,}?(?<!\bBros)[.!?](?=\s|$)/.exec(description)?.[0] ?? description
  const withCheck = `${first} Check if ${name} is down from ${EDGE_LOCATION_COUNT} locations.`
  if (withCheck.length <= DESCRIPTION_MAX) return withCheck
  return clampDescription(first)
}

export function downPageHead(rawDomain: string, site?: DownSite) {
  const domain = site ? rawDomain.trim().toLowerCase() : rawDomain
  const name = site?.name ?? domain
  const url = `${BASE_URL}/tools/is-website-down/${domain}`
  const title = downPageTitle(name, Boolean(site?.statusPage))
  const description = site
    ? downPageMetaDescription(name, site.description)
    : clampDescription(
        `Is ${name} down? Check ${domain} from ${EDGE_LOCATION_COUNT} locations on six continents at once, with response times per location. Free checker, no signup.`,
      )
  return {
    meta: [
      { title },
      { name: 'description', content: description },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: url },
      { property: 'og:image', content: `${BASE_URL}/og/tools-is-website-down.png` },
      // Only curated services are indexed; any other domain is a one-off check.
      ...(site ? [] : [{ name: 'robots', content: 'noindex' }]),
    ],
    links: [{ rel: 'canonical', href: url }],
    scripts: site
      ? [
          jsonLdScript(webPage({ name: title, description, url })),
          jsonLdScript(faqPage(downPageFaq(name, domain, site.statusPage))),
        ]
      : [],
  }
}
